// src/pages/AdminDashboard.js - 新能源智慧微电网能源管理系统大屏
/* global echarts: readonly */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Tag, Select } from 'antd';
import { api } from '../services/api';

// 深蓝色科技工业风格配色
const COLORS = {
  primary: '#00d4ff',
  solar: '#f4c542',
  storage: '#52e8b0',
  load: '#ff5a7f',
  grid: '#3a8dde',
  diesel: '#7ed957',
  wind: '#00b4d8',
  bg: '#0a192f',
  bgLight: '#0a1a33',
  card: '#0d1f44',
  border: 'rgba(0, 180, 216, 0.3)',
  text: '#ffffff',
  textMuted: 'rgba(255,255,255,0.6)',
  // 电价时段色
  valley: '#52c41a',    // 谷段 绿
  flat: '#3a8dde',       // 平段 蓝
  peak: '#fa8c16',       // 峰段 橙
  sharp: '#f5222d',      // 尖峰 红
};


// 模块标题组件
const ModuleTitle = ({ title, subtitle, borderColor = COLORS.primary, extra }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12, paddingBottom: 8,
    borderBottom: `1px solid ${COLORS.border}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 4, height: 16, background: borderColor, borderRadius: 2 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 1 }}>{title}</span>
      {subtitle && <span style={{ fontSize: 11, color: COLORS.textMuted }}>{subtitle}</span>}
    </div>
    {extra}
  </div>
);

// 卡片容器
const ModuleCard = ({ children, style, noPadding = false }) => (
  <div style={{
    background: `linear-gradient(135deg, ${COLORS.card} 0%, #132c5e 100%)`,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: noPadding ? 0 : 16,
    boxShadow: `0 0 20px rgba(0, 180, 216, 0.1)`,
    height: '100%',
    ...style,
  }}>
    {children}
  </div>
);

// 环形进度/饼图
const RingChart = ({ value, max, size = 100, color = COLORS.primary, label, subLabel }) => {
  const percent = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 0.5s' }} />
      </svg>
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)', textAlign: 'center'
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, lineHeight: 1 }}>{value.toFixed(1)}</div>
        <div style={{ fontSize: 10, color: COLORS.textMuted }}>{label}</div>
      </div>
      {subLabel && <div style={{ fontSize: 9, color: COLORS.textMuted, textAlign: 'center', marginTop: 2 }}>{subLabel}</div>}
    </div>
  );
};

// 状态指示灯
const StatusDot = ({ status }) => {
  const { t } = useTranslation();
  const getStatusConfig = (t) => ({
    'online': { color: '#52c41a', label: t('admin.statusOnline') },
    'standby': { color: '#faad14', label: t('admin.statusStandby') },
    'offline': { color: '#999', label: t('admin.statusOffline') },
    'fault': { color: '#ff4d4f', label: t('admin.statusFault') },
  });
  const config = getStatusConfig(t);
  const c = config[status] || config['offline'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, boxShadow: `0 0 6px ${c.color}` }} />
      <span style={{ fontSize: 12, color: COLORS.textMuted }}>{c.label}</span>
    </div>
  );
};

// ===== 主组件 =====
const AdminDashboard = () => {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [envData, setEnvData] = useState({});
  const [powerStats, setPowerStats] = useState({ today: 0, month: 0, todayPv: 0, todayStorage: 0, todayGrid: 0, monthPv: 0, monthStorage: 0, monthGrid: 0 });
  const [storageStats, setStorageStats] = useState({
    todayPeak: 0, todayValley: 0, todayCharge: 0, todayGridExport: 0,
    monthPeak: 0, monthValley: 0, monthCharge: 0, monthGridExport: 0,
  });
  const [loadCurveData, setLoadCurveData] = useState([]);
  const [strategyData, setStrategyData] = useState([]);
  const [gridConfig, setGridConfig] = useState([
    { labelKey: 'admin.gridCapacity', unit: 'KVA', value: 0 },
    { labelKey: 'admin.storagePower', unit: 'KW', value: 0 },
    { labelKey: 'admin.storageCapacity', unit: 'KWh', value: 0 },
    { labelKey: 'admin.pvPower', unit: 'KW', value: 0 },
    { labelKey: 'admin.windPower', unit: 'KW', value: 0 },
    { labelKey: 'admin.dieselPower', unit: 'KW', value: 0 },
    { labelKey: 'admin.dieselCapacity', unit: 'KWh', value: 0 },
  ]);
  const [dashboardData, setDashboardData] = useState({});
  // 设备选择相关（localStorage 持久化）
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(() => {
    const saved = localStorage.getItem('dashboard_selected_device');
    return saved ? Number(saved) : null;
  });
  const selectedDeviceIdRef = useRef(null);
  const [telemetry, setTelemetry] = useState({});
  const [alarmStats, setAlarmStats] = useState({ total: 0, pending: 0, today_count: 0 });
  // 子设备 global_no 选择（localStorage 持久化）
  const [subDeviceFilters, setSubDeviceFilters] = useState(() => {
    const saved = localStorage.getItem('dashboard_sub_device_filters');
    return saved ? JSON.parse(saved) : {};
  });
  const [subDeviceOptions, setSubDeviceOptions] = useState({}); // { mppt: ['MPPT1', 'MPPT2'], ... }

  // 同步 ref
  useEffect(() => { selectedDeviceIdRef.current = selectedDeviceId; }, [selectedDeviceId]);

  // 站点选择持久化到 localStorage
  useEffect(() => {
    if (selectedDeviceId) {
      localStorage.setItem('dashboard_selected_device', selectedDeviceId);
    }
  }, [selectedDeviceId]);

  // 子设备下拉选择持久化到 localStorage
  useEffect(() => {
    localStorage.setItem('dashboard_sub_device_filters', JSON.stringify(subDeviceFilters));
  }, [subDeviceFilters]);

  // 按 global_no 过滤子设备数据，只取最新一条
  const getSubData = (typeKey, telemetryData) => {
    const items = telemetryData?.[typeKey] || [];
    const selectedNo = subDeviceFilters[typeKey];
    if (!selectedNo || items.length === 0) return items.slice(0, 1);
    if (typeof selectedNo !== 'string') return items.slice(0, 1);
    const filtered = items.filter(it => it.global_no === selectedNo);
    return filtered.length > 0 ? filtered.slice(0, 1) : items.slice(0, 1);
  };

  const loadCurveRef = useRef(null);
  const strategyChartRef = useRef(null);
  const loadCurveChartRef = useRef(null);
  const strategyChartRef2 = useRef(null);

  // 实时时钟
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 初始化图表
  useEffect(() => {
    // 延迟100ms确保DOM已渲染完成
    const timer = setTimeout(() => {
      fetchDashboardData();
    }, 100);
    // 定时刷新数据（每30秒）
    const refreshTimer = setInterval(() => {
      fetchDashboardData();
      loadTelemetry();
      loadAlarmStats();
    }, 30000);
    return () => { clearTimeout(timer); clearInterval(refreshTimer); };
  }, []);

  // 初始化gridConfig（只填初始值，label使用key渲染时翻译）
  useEffect(() => {
    setGridConfig([
      { labelKey: 'admin.gridCapacity', unit: 'KVA', value: 0 },
      { labelKey: 'admin.storagePower', unit: 'KW', value: 0 },
      { labelKey: 'admin.storageCapacity', unit: 'KWh', value: 0 },
      { labelKey: 'admin.pvPower', unit: 'KW', value: 0 },
      { labelKey: 'admin.windPower', unit: 'KW', value: 0 },
      { labelKey: 'admin.dieselPower', unit: 'KW', value: 0 },
      { labelKey: 'admin.dieselCapacity', unit: 'KWh', value: 0 },
    ]);
  }, []);

  // ECharts 响应式 resize
  useEffect(() => {
    const handleResize = () => {
      loadCurveChartRef.current?.resize();
      strategyChartRef2.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 加载用户可访问的设备列表
  const loadDeviceList = async () => {
    try {
      const json = await api.getDeviceInfoList({ pageSize: 200 });
      if (json.success && json.data.length > 0) {
        setDeviceList(json.data);
        // 默认选中第一个
        if (!selectedDeviceId) {
          setSelectedDeviceId(json.data[0].id);
        }
      }
    } catch (e) { console.error('loadDeviceList error:', e); }
  };

  // 加载遥测数据（按选中设备过滤）
  const loadTelemetry = async () => {
    try {
      const json = await api.getTelemetryAllLatest();
      if (json.success) {
        const devId = selectedDeviceIdRef.current;
        const filtered = {};
        const filters = {};
        const newOptions = {};
        Object.keys(json.data).forEach(key => {
          const items = json.data[key];
          if (Array.isArray(items)) {
            const devItems = devId ? items.filter(item => Number(item.device_info_id) === Number(devId)) : [];
            filtered[key] = devItems;
            const globalNos = [...new Set(devItems.map(it => it.global_no).filter(Boolean))];
            if (globalNos.length > 0) {
              filters[key] = globalNos[0];
              newOptions[key] = globalNos;
            }
          }
        });
        setTelemetry(filtered);
        setSubDeviceFilters(filters);
        // 以 devId 为 key 缓存各设备的选项，切换设备时正确切换
        setSubDeviceOptions(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(k => { if (!(k in newOptions)) delete next[k]; });
          return { ...next, ...newOptions };
        });
      }
    } catch (e) { console.error('loadTelemetry error:', e); }
  };

  // 加载告警统计
  const loadAlarmStats = async () => {
    try {
      const json = await api.getAlarmRecordStats();
      if (json.success) setAlarmStats(json.data);
    } catch (e) { console.error('loadAlarmStats error:', e); }
  };

  // 设备切换时刷新数据
  useEffect(() => {
    if (selectedDeviceId) {
      loadTelemetry();
    }
  }, [selectedDeviceId]);

  // 初始化加载
  useEffect(() => {
    loadDeviceList();
  }, []);

  useEffect(() => {
    loadAlarmStats();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashRes, energyRes, loadCurveRes, strategyRes] = await Promise.all([
        api.getDashboardOverview(),
        api.getDashboardEnergyStats(),
        api.getDashboardLoadCurve(),
        api.getDashboardStrategy(),
      ]);

      // 大屏综合数据
      if (dashRes.success) {
        const d = dashRes;

        // 环境数据
        setEnvData(d.env || {});

        // 储能峰谷
        if (d.storageStats) {
          setStorageStats({
            todayPeak: d.storageStats.today?.peak || 0,
            todayValley: d.storageStats.today?.valley || 0,
            todayCharge: d.storageStats.today?.charge || 0,
            todayGridExport: d.storageStats.today?.gridExport || 0,
            monthPeak: d.storageStats.month?.peak || 0,
            monthValley: d.storageStats.month?.valley || 0,
            monthCharge: d.storageStats.month?.charge || 0,
            monthGridExport: d.storageStats.month?.gridExport || 0,
          });
        }

        // 电网配置
        if (d.gridConfig) {
          setGridConfig([
            { labelKey: 'admin.gridCapacity', unit: 'KVA', value: parseFloat(d.gridConfig.grid_capacity) || 0 },
            { labelKey: 'admin.storagePower', unit: 'KW', value: parseFloat(d.gridConfig.storage_power) || 0 },
            { labelKey: 'admin.storageCapacity', unit: 'KWh', value: parseFloat(d.gridConfig.storage_capacity) || 0 },
            { labelKey: 'admin.pvPower', unit: 'KW', value: parseFloat(d.gridConfig.pv_power) || 0 },
            { labelKey: 'admin.windPower', unit: 'KW', value: parseFloat(d.gridConfig.wind_power) || 0 },
            { labelKey: 'admin.dieselPower', unit: 'KW', value: parseFloat(d.gridConfig.diesel_power) || 0 },
            { labelKey: 'admin.dieselCapacity', unit: 'KWh', value: parseFloat(d.gridConfig.diesel_capacity) || 0 },
          ]);
        }

        // 子系统关键数据（已删除）

        // 控制器分组数据
        if (d.controllerGroups) {
          setDashboardData(d);
        }

      }

      // 电量统计（饼图）
      if (energyRes.success) {
        const e = energyRes;
        const todayPieSum = (e.today?.pv || 0) + (e.today?.storage || 0) + (e.today?.grid || 0);
        const monthPieSum = (e.month?.pv || 0) + (e.month?.storage || 0) + (e.month?.grid || 0);
        setPowerStats({
          today: todayPieSum,
          month: monthPieSum,
          todayPv: e.today?.pv || 0,
          todayStorage: e.today?.storage || 0,
          todayGrid: e.today?.grid || 0,
          monthPv: e.month?.pv || 0,
          monthStorage: e.month?.storage || 0,
          monthGrid: e.month?.grid || 0,
        });
      }

      // 负载曲线
      if (loadCurveRes.success && loadCurveRes.data && loadCurveRes.data.length > 0) {
        setLoadCurveData(loadCurveRes.data.map(d => ({
          hour: d.hour?.length > 5 ? d.hour?.substring(11, 16) : d.hour,
          total: parseFloat(d.load) || 0,
          grid: parseFloat(d.load) * 0.3 || 0,
          storage: parseFloat(d.storage) || 0,
          emergency: parseFloat(d.pv) * 0.05 || 0,
        })));
        initLoadCurveChart(loadCurveRes.data);
      }

      // 运行策略
      if (strategyRes.success && strategyRes.data && strategyRes.data.length > 0) {
        initStrategyChart(strategyRes.data);
      }

    } catch (e) {
      console.error(t('admin.fetchFailed'), e);
    }
  };

  // 负载特性曲线图
  const initLoadCurveChart = (data) => {
    if (!loadCurveRef.current) return;
    if (loadCurveChartRef.current) loadCurveChartRef.current.dispose();
    const chart = echarts.init(loadCurveRef.current);
    loadCurveChartRef.current = chart;
    // hour 可能是 "2026-07-09 08:00"（API）或 "08:00"（模拟）
    const hours = data.map(d => d.hour?.length > 5 ? d.hour?.substring(11, 16) : d.hour);
    chart.setOption({
      backgroundColor: 'transparent',
      grid: { top: 30, right: 20, bottom: 30, left: 50 },
      legend: {
        data: [t('admin.totalLoad'), t('admin.gridBackup'), t('admin.storageBackup'), t('admin.emergencyGenerator')],
        textStyle: { color: COLORS.textMuted, fontSize: 10 },
        top: 0, right: 0,
      },
      tooltip: { trigger: 'axis', backgroundColor: COLORS.card, borderColor: COLORS.border, textStyle: { color: COLORS.text } },
      xAxis: {
        type: 'category', data: hours,
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 9 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value', name: 'kW', nameTextStyle: { color: COLORS.textMuted },
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 9 },
        splitLine: { lineStyle: { color: 'rgba(0,180,216,0.1)' } },
      },
      series: [
        { name: t('admin.totalLoad'), type: 'line', smooth: true, data: data.map(d => parseFloat(d.load) || 0), lineStyle: { color: COLORS.load, width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(255,90,127,0.4)' }, { offset: 1, color: 'rgba(255,90,127,0.05)' }]) } },
        { name: t('admin.gridBackup'), type: 'line', smooth: true, data: data.map(d => parseFloat(d.load) * 0.3 || 0), lineStyle: { color: COLORS.grid, width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(58,141,222,0.4)' }, { offset: 1, color: 'rgba(58,141,222,0.05)' }]) } },
        { name: t('admin.storageBackup'), type: 'line', smooth: true, data: data.map(d => parseFloat(d.storage) || 0), lineStyle: { color: COLORS.storage, width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(82,232,176,0.4)' }, { offset: 1, color: 'rgba(82,232,176,0.05)' }]) } },
        { name: t('admin.emergencyGenerator'), type: 'line', smooth: true, data: data.map(d => parseFloat(d.pv) * 0.05 || 0), lineStyle: { color: COLORS.solar, width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(244,197,66,0.4)' }, { offset: 1, color: 'rgba(244,197,66,0.05)' }]) } },
      ],
    });
  };

  // 运行策略堆叠图
  const initStrategyChart = (data) => {
    if (!strategyChartRef.current) return;
    if (strategyChartRef2.current) strategyChartRef2.current.dispose();
    const chart = echarts.init(strategyChartRef.current);
    strategyChartRef2.current = chart;
    const hours = data.map(d => d.hour?.length > 5 ? d.hour?.substring(11, 16) : d.hour);
    chart.setOption({
      backgroundColor: 'transparent',
      grid: { top: 30, right: 20, bottom: 30, left: 50 },
      legend: {
        data: [t('admin.gridValley'), t('admin.gridFlat'), t('admin.gridPeak'), t('admin.gridSharp'), t('admin.storageCharge'), t('admin.storageDischarge')],
        textStyle: { color: COLORS.textMuted, fontSize: 9 }, top: 0, right: 0,
      },
      tooltip: { trigger: 'axis', backgroundColor: COLORS.card, borderColor: COLORS.border, textStyle: { color: COLORS.text } },
      xAxis: {
        type: 'category', data: hours,
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 9 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value', name: 'kW', nameTextStyle: { color: COLORS.textMuted },
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 9 },
        splitLine: { lineStyle: { color: 'rgba(0,180,216,0.1)' } },
      },
      series: [
        { name: t('admin.gridValley'), type: 'bar', stack: 'total', data: data.map((_, i) => i % 3 === 0 ? 80 : 0), itemStyle: { color: COLORS.valley } },
        { name: t('admin.gridFlat'), type: 'bar', stack: 'total', data: data.map((_, i) => i % 3 === 1 ? 120 : 0), itemStyle: { color: COLORS.flat } },
        { name: t('admin.gridPeak'), type: 'bar', stack: 'total', data: data.map((_, i) => i % 3 === 2 ? 160 : 0), itemStyle: { color: COLORS.peak } },
        { name: t('admin.gridSharp'), type: 'bar', stack: 'total', data: data.map(() => 0), itemStyle: { color: COLORS.sharp } },
        { name: t('admin.storageCharge'), type: 'bar', stack: 'total', data: data.map(d => parseFloat(d.storage) * 0.5 || 0), itemStyle: { color: COLORS.storage } },
        { name: t('admin.storageDischarge'), type: 'bar', stack: 'total', data: data.map(d => parseFloat(d.storage) * 0.3 || 0), itemStyle: { color: '#2e8b57' } },
        { name: t('admin.totalLoad'), type: 'line', data: data.map(d => parseFloat(d.load) || 0), lineStyle: { color: COLORS.load, width: 2 }, symbol: 'none', markPoint: { symbol: 'pin', symbolSize: 50, data: [{ type: 'max', name: 'Peak' }], label: { formatter: '{c} kW', color: '#fff', fontSize: 9 }, itemStyle: { color: COLORS.load } } },
      ],
    });
  };

  // 周几
  const weekDay = [t('admin.sunday'), t('admin.monday'), t('admin.tuesday'), t('admin.wednesday'), t('admin.thursday'), t('admin.friday'), t('admin.saturday')][currentTime.getDay()];

  return (
    <div style={{ minHeight: '100%', background: `linear-gradient(135deg, ${COLORS.bgLight} 0%, ${COLORS.bg} 50%, ${COLORS.bgLight} 100%)`, padding: 16 }}>
      {/* ===== 顶部标题栏 ===== */}
      <div style={{
        background: `linear-gradient(90deg, rgba(0,180,216,0.15), rgba(0,180,216,0.05), rgba(0,180,216,0.15))`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8, padding: '12px 24px', marginBottom: 16,
      }} className="dashboard-topbar">
        {/* 左侧品牌 */}
        <div className="dashboard-topbar-brand">
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, letterSpacing: 2, whiteSpace: 'nowrap' }}>{t('admin.systemTitle')}</div>
        </div>

        {/* 设备选择器 */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Select
            value={selectedDeviceId}
            onChange={setSelectedDeviceId}
            style={{ width: 240 }}
            placeholder={t('admin.selectDevice')}
            options={deviceList.map(d => ({ value: d.id, label: `${d.sn} ${d.station_name || ''}` }))}
          />
        </div>

        {/* 右侧时间和环境 */}
        <div className="dashboard-topbar-right">
          {/* 环境数据 */}
          <div className="dashboard-topbar-env">
            <span>{t('admin.irradiance')} <span style={{ color: COLORS.solar }}>{envData.irradiance} W/㎡</span></span>
            <span>{t('admin.windSpeed')} <span style={{ color: COLORS.wind }}>{envData.windSpeed} m/s</span></span>
            <span>{t('admin.temperature')} <span style={{ color: COLORS.load }}>{envData.temp} ℃</span></span>
            <span>{t('admin.humidity')} <span style={{ color: COLORS.grid }}>{envData.humidity} %</span></span>
          </div>
          {/* 时钟 */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary, fontFamily: 'monospace' }}>
              {currentTime.toLocaleTimeString('zh-CN', { hour12: false })}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted }}>
              {currentTime.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })} {weekDay}
            </div>
          </div>
        </div>
      </div>

      {/* ===== 8大模块Grid布局 ===== */}
      <Row gutter={[12, 12]}>
        {/* ===== 模块1: 左上 - 电网配置 ===== */}
        <Col span={24} lg={6}>
          <ModuleCard>
            <ModuleTitle title={t('admin.gridConfig')} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {gridConfig.map((item, idx) => (
                <div key={idx} style={{
                  background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: '8px 10px',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t(item.labelKey)}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginTop: 2 }}>{item.value} {item.unit}</div>
                </div>
              ))}
            </div>
          </ModuleCard>
        </Col>

        {/* ===== 模块2: 中上 - 电量统计双饼图 ===== */}
        <Col span={24} lg={10}>
          <ModuleCard>
            <ModuleTitle title={t('admin.powerStats')} />
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>{t('admin.todayPower')}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{powerStats.today.toFixed(1)} <span style={{ fontSize: 11 }}>kWh</span></div>
                  <div style={{ width: 120, height: 120, margin: '8px auto', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <RingChart value={powerStats.today} max={Math.max(powerStats.today, 1)} size={100} color={COLORS.primary} label="kWh" />
                  </div>
                  {/* 简单图例 */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.solar }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.pvGeneration')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.storage }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.storageDischarge')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.grid }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.gridDown')}</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>{t('admin.monthPower')}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.solar }}>{powerStats.month.toFixed(1)} <span style={{ fontSize: 11 }}>kWh</span></div>
                  <div style={{ width: 120, height: 120, margin: '8px auto', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <RingChart value={powerStats.month} max={50000} size={100} color={COLORS.solar} label="kWh" />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.solar }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.pvGeneration')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.storage }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.storageDischarge')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.grid }} />
                      <span style={{ color: COLORS.textMuted }}>{t('admin.gridDown')}</span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </ModuleCard>
        </Col>

        {/* ===== 模块3: 右上 - 储能充放/峰谷电量 ===== */}
        <Col span={24} lg={8}>
          <ModuleCard>
            <ModuleTitle title={t('admin.storageChargeDischarge')} />
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.todayPeakShaving')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.peak }}>{storageStats.todayPeak} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.todayValleyFilling')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.valley }}>{storageStats.todayValley} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.todayStorageCharge')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.storage }}>{storageStats.todayCharge} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.todayGridExport')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.grid }}>{storageStats.todayGridExport || '-'} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.monthPeakShaving')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.peak }}>{storageStats.monthPeak} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.monthValleyFilling')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.valley }}>{storageStats.monthValley} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.monthStorageCharge')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.storage }}>{storageStats.monthCharge} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{t('admin.monthGridExport')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.grid }}>{storageStats.monthGridExport} <span style={{ fontSize: 10 }}>kWh</span></div>
                </div>
              </Col>
            </Row>
          </ModuleCard>
        </Col>

      </Row>

      {/* ===== 子系统关键指标卡片 ===== */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        {/* BMS */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.bmsKeyData') || 'BMS电池'} borderColor={COLORS.storage} />
            {(telemetry.bms || []).length === 0 ? (
              <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '20px 0' }}>-</div>
            ) : (
              (telemetry.bms || []).slice(0, 1).map((b, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.socLabel')}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.storage }}>{b.soc != null ? `${b.soc}%` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.rackVoltage')}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{b.voltage_v != null ? `${b.voltage_v}V` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.rackCurrent')}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{b.current_a != null ? `${b.current_a}A` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.bmsPower') || '功率'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{b.power_kw != null ? `${b.power_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.maxCellVoltage') || '最高电芯电压'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{b.max_cell_voltage_mv != null ? `${(b.max_cell_voltage_mv / 1000).toFixed(3)}V` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.minCellVoltage') || '最低电芯电压'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{b.min_cell_voltage_mv != null ? `${(b.min_cell_voltage_mv / 1000).toFixed(3)}V` : '-'}</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {b.online != null ? <Tag color={b.online === 1 ? 'green' : 'default'} style={{ fontSize: 10 }}>{b.online === 1 ? t('admin.online') : t('admin.offlineLabel')}</Tag> : '-'}
                  </div>
                </div>
              ))
            )}
          </ModuleCard>
        </Col>

        {/* PCS */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.pcsKeyData') || 'PCS变流器'} borderColor={COLORS.solar} />
            {(subDeviceOptions.pcs || []).length > 1 && (
              <div style={{ marginBottom: 8 }}>
                <Select
                  size="small"
                  value={subDeviceFilters.pcs}
                  onChange={v => setSubDeviceFilters(f => ({ ...f, pcs: v }))}
                  style={{ width: '100%' }}
                  dropdownStyle={{ minWidth: 80 }}
                  options={subDeviceOptions.pcs.map(no => ({ value: no, label: no }))}
                />
              </div>
            )}
            {getSubData('pcs', telemetry).length === 0 ? (
              <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '20px 0' }}>-</div>
            ) : (
              getSubData('pcs', telemetry).map((p, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.activePower') || '有功功率'}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.solar }}>{p.active_power_kw != null ? `${p.active_power_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.reactivePower') || '无功功率'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{p.reactive_power_kvar != null ? `${p.reactive_power_kvar}kVar` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.pcsVoltage') || 'Uab'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{p.ac_voltage_ab_v != null ? `${p.ac_voltage_ab_v}V` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.pcsCurrent') || '电流'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{p.ac_current_a_a != null ? `${p.ac_current_a_a}A` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.onOffState') || '开关机'}</span>
                    <span style={{ fontSize: 13, color: p.onoff_state === 1 ? COLORS.storage : COLORS.load }}>{p.onoff_state === 1 ? '开机' : (p.onoff_state === 0 ? '关机' : '-')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.gridConnected') || '并网'}</span>
                    <span style={{ fontSize: 13, color: p.grid_state === 1 ? COLORS.storage : COLORS.load }}>{p.grid_state === 1 ? '并网' : (p.grid_state === 0 ? '离网' : '-')}</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {p.online != null ? <Tag color={p.online === 1 ? 'green' : 'default'} style={{ fontSize: 10 }}>{p.online === 1 ? t('admin.online') : t('admin.offlineLabel')}</Tag> : '-'}
                  </div>
                </div>
              ))
            )}
          </ModuleCard>
        </Col>

        {/* Meter */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.meterKeyData') || '电表'} borderColor={COLORS.grid} />
            {(subDeviceOptions.meter || []).length > 1 && (
              <div style={{ marginBottom: 8 }}>
                <Select
                  size="small"
                  value={subDeviceFilters.meter}
                  onChange={v => setSubDeviceFilters(f => ({ ...f, meter: v }))}
                  style={{ width: '100%' }}
                  dropdownStyle={{ minWidth: 80 }}
                  options={subDeviceOptions.meter.map(no => ({ value: no, label: no }))}
                />
              </div>
            )}
            {getSubData('meter', telemetry).length === 0 ? (
              <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '20px 0' }}>-</div>
            ) : (
              getSubData('meter', telemetry).map((m, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.activePower') || '有功功率'}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.grid }}>{m.active_power_kw != null ? `${m.active_power_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.phasePower') || 'A相功率'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.a_phase_power_kw != null ? `${m.a_phase_power_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.phaseVoltage') || '电压'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.a_phase_voltage_v != null ? `${m.a_phase_voltage_v}V` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.powerFactor') || '功率因数'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.power_factor != null ? m.power_factor : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.frequencyHz') || '频率'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.frequency_hz != null ? `${m.frequency_hz}Hz` : '-'}</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {m.online != null ? <Tag color={m.online === 1 ? 'green' : 'default'} style={{ fontSize: 10 }}>{m.online === 1 ? t('admin.online') : t('admin.offlineLabel')}</Tag> : '-'}
                  </div>
                </div>
              ))
            )}
          </ModuleCard>
        </Col>

        {/* MPPT */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.mpptKeyData') || 'MPPT'} borderColor={COLORS.solar} />
            {(subDeviceOptions.mppt || []).length > 1 && (
              <div style={{ marginBottom: 8 }}>
                <Select
                  size="small"
                  value={subDeviceFilters.mppt}
                  onChange={v => setSubDeviceFilters(f => ({ ...f, mppt: v }))}
                  style={{ width: '100%' }}
                  dropdownStyle={{ minWidth: 80 }}
                  options={subDeviceOptions.mppt.map(no => ({ value: no, label: no }))}
                />
              </div>
            )}
            {getSubData('mppt', telemetry).length === 0 ? (
              <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '20px 0' }}>-</div>
            ) : (
              getSubData('mppt', telemetry).map((m, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.mpptPower') || 'MPPT功率'}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.solar }}>{m.mppt_power_kw != null ? `${m.mppt_power_kw}kW` : (m.current_power_kw != null ? `${m.current_power_kw}kW` : '-')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.curLimit') || '当前限值'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.cur_limit_kw != null ? `${m.cur_limit_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.powerLimit') || '功率限值'}</span>
                    <span style={{ fontSize: 13, color: COLORS.text }}>{m.power_limit_kw != null ? `${m.power_limit_kw}kW` : '-'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.limitState') || '限功率状态'}</span>
                    <span style={{ fontSize: 13, color: m.limit_state === 1 ? COLORS.load : COLORS.text }}>{m.limit_state === 1 ? t('admin.limitActive') || '受限' : (m.limit_state === 0 ? t('admin.normal') || '正常' : '-')}</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                    {m.online != null ? <Tag color={m.online === 1 ? 'green' : 'default'} style={{ fontSize: 10 }}>{m.online === 1 ? t('admin.online') : t('admin.offlineLabel')}</Tag> : '-'}
                  </div>
                </div>
              ))
            )}
          </ModuleCard>
        </Col>

        {/* Report (环境/消防/门/水浸) */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.fireAlarmData') || '消防/门/水浸'} borderColor={COLORS.load} />
            {(telemetry.report || []).length === 0 ? (
              <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '20px 0' }}>-</div>
            ) : (
              (() => {
                const seen = new Set();
                const unique = (telemetry.report || []).filter(r => {
                  const key = r.name || r.device_type || r.global_no;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                }).slice(0, 5);
                return unique.map((r, i) => {
                  const isFire = r.device_type?.toLowerCase() === 'fire';
                  const smokeAlarm = Boolean(r.smoke_alarm);
                  const heatAlarm = Boolean(r.heat_alarm);
                  const fireAlarm = Boolean(r.fire_alarm);
                  const waterAlarm = Boolean(r.water_leak);
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>{r.name || r.device_type || r.global_no}</span>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: smokeAlarm ? '#ff4d4f' : '#52c41a', color: '#fff' }}>烟</span>
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: heatAlarm ? '#ff4d4f' : '#52c41a', color: '#fff' }}>温</span>
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: fireAlarm ? '#ff4d4f' : '#52c41a', color: '#fff' }}>火</span>
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: waterAlarm ? '#ff4d4f' : '#52c41a', color: '#fff' }}>水</span>
                        {isFire && r.temperature_c != null && (
                          <span style={{ fontSize: 10, color: COLORS.textMuted }}>{r.temperature_c}℃</span>
                        )}
                      </div>
                    </div>
                  );
                });
              })()
            )}
          </ModuleCard>
        </Col>

        {/* 告警统计 */}
        <Col span={24} md={8} lg={4}>
          <ModuleCard>
            <ModuleTitle title={t('admin.alarmStats') || '告警统计'} borderColor={COLORS.load} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.totalAlarms') || '总告警'}</span>
                <Tag color={alarmStats.total > 0 ? 'orange' : 'green'} style={{ fontWeight: 600 }}>{alarmStats.total || 0}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.pendingAlarms') || '待处理'}</span>
                <Tag color={alarmStats.pending > 0 ? 'red' : 'green'} style={{ fontWeight: 600 }}>{alarmStats.pending || 0}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>{t('admin.todayAlarms') || '今日新增'}</span>
                <Tag color={alarmStats.today_count > 0 ? 'blue' : 'green'} style={{ fontWeight: 600 }}>{alarmStats.today_count || 0}</Tag>
              </div>
            </div>
          </ModuleCard>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;
